<template>
  <SoDialog
    v-model="value"
    :max-width="1366"
    :loading="current.directDebit.isLoading"
    card-title="Direct Debit Mandates"
  >
    <SoErrorBoundary>
      <template v-if="!current.directDebit.isLoading">
        <SoAlert v-if="!current.directDebit.data.length" type="info">
          No direct debit mandate information available
        </SoAlert>
        <SoDataTable
          v-else
          :headers="headers"
          :items="mandatesList"
          :searchable="false"
          :has-pagination="false"
          :sort-by="[{ key: 'createdDttm', order: 'desc' }]"
        />
      </template>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
  } from "@/types/components/SoDataTable"
  import { IDirectDebitBankAccountSummary } from "@/models/directDebit/directDebit.types"

  export default defineComponent({
    components: {
      SoDialog,
      SoDataTable,
      SoAlert,
      SoErrorBoundary,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        headers: [
          {
            title: "ID",
            key: "id",
            sortable: false,
          },
          {
            title: "Payment Method Type",
            key: "paymentMethodType",
            sortable: false,
          },
          {
            title: "Status",
            key: "status",
            sortable: false,
          },
          {
            title: "Created Dttm",
            key: "createdDttm",
            type: SoDataTableColumnType.DATE,
            sortable: true,
          },
          {
            title: "From Dttm",
            key: "fromDttm",
            type: SoDataTableColumnType.DATE,
            sortable: true,
          },
          {
            title: "Default Status",
            key: "defaultStatus",
            sortable: false,
          },
          {
            title: "Bank Account",
            key: "bankAccount",
            sortable: false,
            type: SoDataTableColumnType.LIST,
          },
        ] as SoDataTableHeader[],
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      mandatesList() {
        return this.current.directDebit.data.map((mandate) => ({
          ...mandate,
          bankAccount: Object.keys(mandate.bankAccount ?? {}).reduce(
            (acc, key: string) => {
              switch (key) {
                case "paymentMethodType":
                  return {
                    ...acc,
                    "Payment Method Type": mandate.bankAccount?.[key],
                  }
                case "accountName":
                  return {
                    ...acc,
                    "Account Name": mandate.bankAccount?.[key],
                  }
                case "accountNumber":
                  return {
                    ...acc,
                    "Account Number": mandate.bankAccount?.[key],
                  }
                case "sortCode":
                  return {
                    ...acc,
                    "Sort Code": mandate.bankAccount?.[key],
                  }
                case "bankReference":
                  return {
                    ...acc,
                    "Bank Ref": mandate.bankAccount?.[key],
                  }
                case "mandateReference":
                  return {
                    ...acc,
                    "Mandate Ref": mandate.bankAccount?.[key],
                  }
              }
              return acc
            },
            {} as IDirectDebitBankAccountSummary,
          ),
        }))
      },
    },
  })
</script>
